import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { fmtDateTimeFirm } from "@/lib/dt";   // 5c · F-5
import React, { useEffect, useState } from "react";
import useEffectEvent from "@/lib/useEffectEvent";   // Step 7c · E3: the effect runs on its own deps only (see the file)
import api, { toErrMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Package, Send, Check, X, Clock } from "lucide-react";

/**
 * Firm-side plan-change request panel:
 * • Partner/Admin can submit a request against any active platform-published plan
 * • The tenant's own past submissions and their status are shown below the form
 */
export default function PlanRequestSection() {
  const colW = useResizableColumns({ storageKey: "plan-request-section", defaults: [110, 160, 240, 160, 110, 240], mins: [72, 100, 130, 100, 72, 130] });   // Step 11d · add-on
  const { user } = useAuth();
  const canRequest = user?.role === "Partner" || user?.role === "Admin";

  const [plans, setPlans] = useState([]);
  const [mine, setMine] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ requested_plan: "", billing_cycle: "monthly", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [p, m] = await Promise.all([
        api.get("/plans"),
        api.get("/plan-requests"),
      ]);
      setPlans(p.data.filter((x) => x.active !== false));
      setMine(m.data);
      if (!form.requested_plan && p.data.length) {
        setForm((f) => ({ ...f, requested_plan: p.data[0].name }));
      }
    } catch { /* silent — nav probably unauthorised */ }
  };
  const loadOnRequest = useEffectEvent(load);
  useEffect(() => { if (canRequest) loadOnRequest(); }, [canRequest, loadOnRequest]);

  if (!canRequest) return null;

  const submit = async () => {
    if (!form.requested_plan) { toast.error("Pick a plan"); return; }
    setBusy(true);
    try {
      await api.post("/plan-requests", form);
      toast.success("Plan-change request submitted");
      setOpen(false);
      setForm({ requested_plan: plans[0]?.name || "", billing_cycle: "monthly", notes: "" });
      await load();
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
    finally { setBusy(false); }
  };

  return (
    <Card data-testid="plan-request-section">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-emerald-600" />
          <CardTitle className="text-base">Subscription plan</CardTitle>
        </div>
        <Button size="sm" onClick={() => setOpen(true)}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="plan-request-open-btn">
          <Send className="me-1 h-3 w-3" /> Request plan change
        </Button>
      </CardHeader>
      <CardContent className="text-sm">
        {mine.length === 0 ? (
          <div className="text-xs text-muted-foreground">
            You haven't submitted any plan-change requests yet.
          </div>
        ) : (
          <div className="overflow-x-auto rounded border">
            <ResizableTable cols={colW} className="w-full text-sm">
              <thead className="border-b bg-slate-50 text-[10px] uppercase text-muted-foreground">
                <tr>
                  <ResizableTh index={0} className="px-3 py-2 text-start">Requested plan</ResizableTh>
                  <ResizableTh index={1} className="px-3 py-2 text-start">Cycle</ResizableTh>
                  <ResizableTh index={2} className="px-3 py-2 text-start">Notes</ResizableTh>
                  <ResizableTh index={3} className="px-3 py-2 text-start">Submitted</ResizableTh>
                  <ResizableTh index={4} className="px-3 py-2 text-start">Status</ResizableTh>
                  <ResizableTh index={5} className="px-3 py-2 text-start">Platform notes</ResizableTh>
                </tr>
              </thead>
              <tbody>
                {mine.map((r) => (
                  <tr key={r.id} className="border-b" data-testid={`plan-req-row-${r.id}`}>
                    <td className="px-3 py-2 font-medium">{r.requested_plan}</td>
                    <td className="px-3 py-2 text-xs">{r.billing_cycle}</td>
                    <td className="px-3 py-2 text-xs max-w-xs break-words">{r.notes || "—"}</td>
                    <td className="px-3 py-2 text-[10px] text-muted-foreground">
                      {fmtDateTimeFirm(r.created_at)}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium ${
                        r.status === "pending" ? "bg-amber-100 text-amber-800"
                        : r.status === "approved" ? "bg-emerald-100 text-emerald-800"
                        : "bg-red-100 text-red-800"}`}>
                        {r.status === "pending" ? <Clock className="h-3 w-3" />
                          : r.status === "approved" ? <Check className="h-3 w-3" />
                          : <X className="h-3 w-3" />}
                        {r.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">{r.reviewer_notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </ResizableTable>
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request a plan change</DialogTitle>
            <DialogDescription>
              Your platform administrator will review your request and either
              apply the new plan or contact you.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">Desired plan</label>
              <Select value={form.requested_plan}
                      onValueChange={(v) => setForm({ ...form, requested_plan: v })}>
                <SelectTrigger data-testid="plan-request-plan-select"><SelectValue placeholder="Pick a plan" /></SelectTrigger>
                <SelectContent>
                  {plans.map((p) => (
                    <SelectItem key={p.id} value={p.name}>
                      {p.name} — ${p.price_monthly}/mo · {p.max_users ?? "∞"} users · {p.max_engagements ?? "∞"} engs
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium">Billing cycle</label>
              <Select value={form.billing_cycle}
                      onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium">Notes (optional)</label>
              <Textarea rows={3} value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        placeholder="Anything the platform administrator should know?"
                        data-testid="plan-request-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy}
                      className="bg-slate-900 hover:bg-slate-800"
                      data-testid="plan-request-submit">
              {busy ? "Submitting…" : "Submit request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
